import React, { useEffect, useRef } from 'react';

/**
 * Grant Spiral Lava Lamp - Orbs actuated by Alpha Theta mathematics
 * Slow-moving colorful orbs creating fractal dynamic background
 */
const GrantSpiralLavaLamp = ({ size = 3000, speed = 0.04 }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationTime = 0;
    const baseAlpha = 1.618; // Golden ratio - Alpha Theta

    // Initialize orbs with Grant spiral positioning
    const orbs = [];
    const numOrbs = 25; // Number of lava lamp orbs
    
    for (let i = 0; i < numOrbs; i++) {
      const theta = (i / numOrbs) * 8 * Math.PI;
      const phase = (i / numOrbs) * 2 * Math.PI;
      
      // Grant formula: R = α⋅π/(2⋅(−θ+3π))
      const denominator = 2 * (-theta + 3 * Math.PI);
      const r = denominator !== 0 ? (baseAlpha * Math.PI) / denominator : 0;
      
      orbs.push({
        baseTheta: theta,
        baseR: Math.abs(r) * 40 + 200, // Base radius from center
        phase: phase,
        size: 40 + Math.random() * 80, // Orb size 40-120px
        colorIndex: i % 5,
        speedMod: 0.8 + Math.random() * 0.4 // Slight speed variation
      });
    }

    const colors = [
      [255, 215, 0, 0.7],     // Gold
      [255, 140, 0, 0.7],     // Orange
      [255, 69, 0, 0.6],      // Red-orange
      [255, 20, 147, 0.6],    // Deep pink
      [138, 43, 226, 0.6]     // Blue violet
    ];

    const drawLavaLamp = (time) => {
      // Clear canvas
      ctx.clearRect(0, 0, size, size);
      
      const centerX = size / 2;
      const centerY = size / 2;

      // Draw each orb
      orbs.forEach((orb, idx) => {
        // Grant spiral motion - slow rotation
        const theta = orb.baseTheta + time * 0.0003 * orb.speedMod;
        const radiusOscillation = Math.sin(time * 0.0005 + orb.phase) * 50;
        const r = orb.baseR + radiusOscillation;
        
        // Calculate position
        const x = centerX + r * Math.cos(theta);
        const y = centerY + r * Math.sin(theta);
        
        // Pulsing size
        const sizePulse = Math.sin(time * 0.001 + orb.phase) * 10;
        const currentSize = orb.size + sizePulse;
        
        // Color with glow
        const color = colors[orb.colorIndex];
        
        // Draw orb with gradient and glow
        const gradient = ctx.createRadialGradient(x, y, 0, x, y, currentSize);
        gradient.addColorStop(0, `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3]})`);
        gradient.addColorStop(0.5, `rgba(${color[0]}, ${color[1]}, ${color[2]}, ${color[3] * 0.5})`);
        gradient.addColorStop(1, `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0)`);
        
        // Glow effect
        ctx.shadowColor = `rgba(${color[0]}, ${color[1]}, ${color[2]}, 0.8)`;
        ctx.shadowBlur = 40;
        
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(x, y, currentSize, 0, 2 * Math.PI);
        ctx.fill();
        
        ctx.shadowBlur = 0;
      });
    };

    const animate = () => {
      drawLavaLamp(animationTime);
      animationTime += speed;
      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [size, speed]);

  return (
    <canvas
      ref={canvasRef}
      width={size}
      height={size}
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'transparent',
        mixBlendMode: 'screen',
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      }}
    />
  );
};

export default GrantSpiralLavaLamp;
